import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider shell for the detail views; the forms keep the default. */
  size?: 'md' | 'lg';
}

/**
 * Shared RTL dialog shell. Full-height sheet on a phone, centred card from
 * `sm:` up — the two shapes the fleet screens need.
 */
export function Modal({ title, subtitle, onClose, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        role="presentation"
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={[
          'relative w-full bg-panel border border-line shadow-xl',
          'rounded-t-2xl sm:rounded-2xl max-h-[92vh] sm:max-h-[86vh] flex flex-col',
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg',
        ].join(' ')}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line p-4 sm:p-5">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">{title}</h2>
            {subtitle ? <div className="mt-0.5 text-sm text-ink/60">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="סגירה"
            className="shrink-0 w-9 h-9 rounded-xl border border-line flex items-center justify-center text-ink/60 hover:text-ink"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        {/* `overscroll-contain`: reaching the end of a long form must not hand
            the swipe to the browser, which would reload the page under it. */}
        <div className="overflow-y-auto overscroll-contain p-4 sm:p-5 grow">{children}</div>

        {footer ? (
          <footer className="border-t border-line p-4 sm:p-5 flex gap-2 justify-end">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}
